import { sharedStyles } from "@lib/sharedStyles";
import { css, CSSResultGroup, html, LitElement } from "lit";
import { customElement, state } from "lit/decorators.js";

interface BuzzedPlayer {
	name: string;
	buzzTime: number;
}

interface BuzzerMessage {
	type: 'team_joined' | 'team_buzzed' | 'lock_buzzers' | 'reset_buzzers';
	teamName?: string;
	timestamp?: number;
	locked?: boolean;
}

@customElement("buzzer-moderator")
class BuzzerModerator extends LitElement {
	static readonly styles: CSSResultGroup = [
		sharedStyles,
		css`
            .container {
                max-width: 800px;
                margin: 0 auto;
                padding: 20px;
            }

            section {
                margin-bottom: 30px;
            }

            .settings {
                display: flex;
                flex-direction: column;
                gap: 10px;
                margin-top: 20px;
            }

            .button-container {
                display: flex;
                justify-content: center;
            }

            .divider {
                border-top: 1px solid #ddd;
                margin: 15px 0;
            }

            .queue ul {
                list-style: none;
                padding: 0;
                margin: 0;
            }

            .queue li {
                margin-bottom: 8px;
            }

            .player-sign {
                background-color: #f8f9fa;
                border: 1px solid #dee2e6;
                border-radius: 4px;
                padding: 10px;
            }

            .resettable .player-sign {
                background-color: #fff3cd;
                border-color: #ffeaa7;
            }

            .buzz-order {
                font-size: 0.8rem;
                color: #666;
                margin-left: 8px;
            }

            .time-diff {
                font-size: 0.7rem;
                color: #999;
                display: block;
                margin-top: 2px;
            }
        `
	];

	@state() private buzzersLocked = false;
	@state() private buzzedPlayers: BuzzedPlayer[] = [];
	@state() private otherPlayers: string[] = [];
	private webSocket: WebSocket | null = null;

	connectedCallback() {
		super.connectedCallback();
		this.initWebSocket();
	}

	disconnectedCallback() {
		super.disconnectedCallback();
		if (this.webSocket) {
			this.webSocket.close();
		}
	}

	private initWebSocket() {
		const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
		const wsUrl = `${protocol}//${window.location.host}/ws/buzzer/`;

		this.webSocket = new WebSocket(wsUrl);

		this.webSocket.onopen = () => {
			console.log('Buzzer moderator WebSocket connected');
		};

		this.webSocket.onmessage = (event) => {
			const message: BuzzerMessage = JSON.parse(event.data);
			this.handleWebSocketMessage(message);
		};

		this.webSocket.onclose = () => {
			console.log('Buzzer moderator WebSocket closed, reconnecting in 3 seconds...');
			setTimeout(() => this.initWebSocket(), 3000);
		};
	}

	private handleWebSocketMessage(message: BuzzerMessage) {
		switch (message.type) {
			case 'team_joined':
				if (message.teamName && !this.otherPlayers.includes(message.teamName)) {
					this.otherPlayers = [...this.otherPlayers, message.teamName];
				}
				break;

			case 'team_buzzed':
				if (message.teamName && message.timestamp) {
					const alreadyBuzzed = this.buzzedPlayers.some(player => player.name === message.teamName);

					if (!alreadyBuzzed) {
						const newBuzzedPlayer = {
							name: message.teamName,
							buzzTime: message.timestamp
						};
						this.buzzedPlayers = [...this.buzzedPlayers, newBuzzedPlayer]
							.sort((a, b) => a.buzzTime - b.buzzTime);
					}
				}
				break;
		}
	}

	private sendWebSocketMessage(message: BuzzerMessage) {
		if (this.webSocket && this.webSocket.readyState === WebSocket.OPEN) {
			this.webSocket.send(JSON.stringify(message));
		}
	}

	private handleLockBuzzers() {
		this.buzzersLocked = !this.buzzersLocked;
		this.sendWebSocketMessage({
			type: 'lock_buzzers',
			locked: this.buzzersLocked
		});
	}

	private handleResetAllBuzzers() {
		this.buzzedPlayers = [];
		this.buzzersLocked = false;

		this.sendWebSocketMessage({
			type: 'reset_buzzers'
		});
	}

	private formatTimeDifference(buzzTime: number): string {
		if (this.buzzedPlayers.length === 0) return '';

		const firstBuzzTime = this.buzzedPlayers[0].buzzTime;
		const diffMs = buzzTime - firstBuzzTime;

		if (diffMs === 0) return '';

		const seconds = Math.floor(diffMs / 1000);
		const milliseconds = diffMs % 1000;

		return `+${seconds}.${milliseconds.toString().padStart(3, '0')}s`;
	}

	render() {
		return html`
            <div class="container">
                <section>
                    <h1>Buzzer Moderator</h1>
                    
                    <div class="settings">
                        <div class="button-container">
                            <basic-button @click="${this.handleLockBuzzers}">
                                ${this.buzzersLocked ? 'Unlock buzzers' : 'Lock buzzers'}
                            </basic-button>
                        </div>
                        <div class="button-container">
                            <basic-button @click="${this.handleResetAllBuzzers}">
                                Reset all buzzers
                            </basic-button>
                        </div>
                        <div class="divider"></div>
                    </div>
                </section>

                <div class="queue">
                    <h2>Players Buzzed</h2>
                    <ul>
                        ${this.buzzedPlayers.map((player, index) => html`
                            <li class="resettable">
                                <div class="player-sign">
                                    <div class="name">
                                        ${player.name}
                                        <span class="buzz-order">#${index + 1}</span>
                                        ${index > 0 ? html`
                                            <span class="time-diff">${this.formatTimeDifference(player.buzzTime)}</span>
                                        ` : ''}
                                    </div>
                                </div>
                            </li>
                        `)}
                    </ul>
                </div>

                <div class="queue">
                    <h2>Other Players</h2>
                    <ul>
                        ${this.otherPlayers.map(player => html`
                            <li>
                                <div class="player-sign">
                                    <div class="name">${player}</div>
                                </div>
                            </li>
                        `)}
                    </ul>
                </div>
            </div>
        `;
	}
}

export default BuzzerModerator;